import React from 'react';

interface MockTeamRosterProps {
    variant?: 'full' | 'compact';
    className?: string;
}

const athletes = [
    { name: 'J. Martinez', pos: 'QB', readiness: 87, hrv: 72 },
    { name: 'A. Chen', pos: 'WR', readiness: 74, hrv: 65 },
    { name: 'R. Williams', pos: 'LB', readiness: 62, hrv: 58 },
    { name: 'K. Johnson', pos: 'RB', readiness: 81, hrv: 70 },
    { name: 'T. Davis', pos: 'CB', readiness: 43, hrv: 41 },
];

const getStatusColor = (readiness: number) => {
    if (readiness >= 75) return '#22C55E';
    if (readiness >= 55) return '#EAB308';
    return '#EF4444';
};

const teamAvgReadiness = Math.round(athletes.reduce((s, a) => s + a.readiness, 0) / athletes.length);
const teamAvgHrv = Math.round(athletes.reduce((s, a) => s + a.hrv, 0) / athletes.length);
const readyCount = athletes.filter(a => a.readiness >= 55).length;

const MockTeamRoster: React.FC<MockTeamRosterProps> = ({ variant = 'full', className = '' }) => {
    const compact = variant === 'compact';
    const viewW = compact ? 360 : 440;
    const viewH = compact ? 200 : 250;
    const rowH = compact ? 22 : 28;
    const headerY = compact ? 24 : 30;
    const tableStartY = compact ? 42 : 52;
    const fontSize = compact ? 8 : 10;
    const headerFontSize = compact ? 9 : 11;
    const barW = compact ? 50 : 70;
    const barH = compact ? 6 : 8;

    // Column x positions
    const colName = 12;
    const colPos = compact ? 110 : 135;
    const colBar = compact ? 145 : 180;
    const colPct = colBar + barW + 6;
    const colHrv = compact ? 260 : 310;
    const colStatus = compact ? 310 : 380;

    return (
        <div className={`w-full ${className}`}>
            <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full h-auto" fill="none">
                {/* Header */}
                <text x={colName} y={headerY - 8} fill="white" fontSize={headerFontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                    TEAM ROSTER
                </text>
                <text x={viewW - 12} y={headerY - 8} textAnchor="end" fill="rgba(255,255,255,0.3)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">
                    TODAY
                </text>

                {/* Column headers */}
                <line x1={colName} y1={headerY} x2={viewW - 12} y2={headerY} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                <text x={colName} y={headerY + 12} fill="rgba(255,255,255,0.4)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">Name</text>
                <text x={colPos} y={headerY + 12} fill="rgba(255,255,255,0.4)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">Pos</text>
                <text x={colBar} y={headerY + 12} fill="rgba(255,255,255,0.4)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">Readiness</text>
                <text x={colHrv} y={headerY + 12} fill="rgba(255,255,255,0.4)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">HRV</text>
                <text x={colStatus} y={headerY + 12} fill="rgba(255,255,255,0.4)" fontSize={fontSize - 1} fontFamily="Inter, sans-serif">Status</text>
                <line x1={colName} y1={headerY + 16} x2={viewW - 12} y2={headerY + 16} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />

                {/* Athlete rows */}
                {athletes.map((athlete, i) => {
                    const y = tableStartY + 16 + i * rowH;
                    const color = getStatusColor(athlete.readiness);
                    const barFillW = (athlete.readiness / 100) * barW;

                    return (
                        <g key={athlete.name}>
                            <text x={colName} y={y + fontSize} fill="rgba(255,255,255,0.7)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                {athlete.name}
                            </text>
                            <text x={colPos} y={y + fontSize} fill="rgba(255,255,255,0.4)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                {athlete.pos}
                            </text>
                            {/* Readiness bar background */}
                            <rect x={colBar} y={y + 2} width={barW} height={barH} fill="rgba(255,255,255,0.06)" />
                            {/* Readiness bar fill */}
                            <rect x={colBar} y={y + 2} width={barFillW} height={barH} fill={color} opacity="0.7" />
                            {/* Percentage */}
                            <text x={colPct} y={y + fontSize} fill={color} fontSize={fontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                                {athlete.readiness}%
                            </text>
                            {/* HRV */}
                            <text x={colHrv} y={y + fontSize} fill="rgba(255,255,255,0.5)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                {athlete.hrv}ms
                            </text>
                            {/* Status dot */}
                            <circle cx={colStatus + 8} cy={y + fontSize - 3} r={compact ? 3 : 4} fill={color} className="animate-data-pulse" />
                            {/* Row separator */}
                            {i < athletes.length - 1 && (
                                <line x1={colName} y1={y + rowH - 2} x2={viewW - 12} y2={y + rowH - 2} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
                            )}
                        </g>
                    );
                })}

                {/* Team average row */}
                {(() => {
                    const avgY = tableStartY + 16 + athletes.length * rowH + 4;
                    const avgColor = getStatusColor(teamAvgReadiness);
                    const avgBarFillW = (teamAvgReadiness / 100) * barW;
                    return (
                        <g>
                            <line x1={colName} y1={avgY - 6} x2={viewW - 12} y2={avgY - 6} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                            <text x={colName} y={avgY + fontSize} fill="white" fontSize={fontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                                TEAM AVG
                            </text>
                            <rect x={colBar} y={avgY + 2} width={barW} height={barH} fill="rgba(255,255,255,0.06)" />
                            <rect x={colBar} y={avgY + 2} width={avgBarFillW} height={barH} fill={avgColor} opacity="0.7" />
                            <text x={colPct} y={avgY + fontSize} fill={avgColor} fontSize={fontSize} fontWeight="600" fontFamily="Inter, sans-serif">
                                {teamAvgReadiness}%
                            </text>
                            <text x={colHrv} y={avgY + fontSize} fill="rgba(255,255,255,0.5)" fontSize={fontSize} fontFamily="Inter, sans-serif">
                                {teamAvgHrv}ms
                            </text>
                            <text x={colStatus} y={avgY + fontSize} fill="rgba(255,255,255,0.5)" fontSize={fontSize} fontWeight="500" fontFamily="Inter, sans-serif">
                                {readyCount}/{athletes.length} GO
                            </text>
                        </g>
                    );
                })()}
            </svg>
        </div>
    );
};

export default MockTeamRoster;
